import * as React from 'react'

import type {
  ErrorLike,
  ObservableQuery,
  OperationVariables,
  RefetchQueriesInclude,
} from '@apollo/client'
import { toErrorLike } from '@apollo/client/errors'
import type { MaybeMasked } from '@apollo/client/masking'
import { useApolloClient, useSubscription } from '@apollo/client/react'
import { getMainDefinition } from '@apollo/client/utilities'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { Kind } from 'graphql'

import { useCallbackRef } from '@buffer-mono/popcorn'

import { logError } from '~publish/legacy/utils/logError'

import { useRealTimeContext } from './RealTimeProvider'
import type { RealTimeStatus } from './realTimeStatus'
import type { RealTimeConnectionListeners } from './sseLink'

import type { DocumentNode } from 'graphql'

/** Why {@link UseBufferRealTimeSubOptions.onReconnect} ran. */
export type RealTimeReconnectDetails = {
  /** `false` on the first connect, `true` after the stream broke. */
  reconnected: boolean
}

export type RefetchOnReconnect =
  | boolean
  | RefetchQueriesInclude
  | ((query: ObservableQuery) => boolean)

export type MaybeMissingVariables<TVariables extends OperationVariables> = {
  [Key in keyof TVariables]: TVariables[Key] | null | undefined
}

/** Required when the subscription declares a required variable, as in `useSubscription`. */
type VariablesOption<TVariables extends OperationVariables> =
  {} extends TVariables
    ? { variables?: MaybeMissingVariables<TVariables> }
    : { variables: MaybeMissingVariables<TVariables> }

export type UseBufferRealTimeSubOptions<
  TData,
  TVariables extends OperationVariables,
> = VariablesOption<TVariables> & {
  /** `true` holds the subscription closed, so the view opens no request. */
  skip?: boolean
  /**
   * Runs for each event the server sends, in order.
   *
   * While the refetch after a connect is in flight, the hook holds the events
   * that arrive and hands them over once it settles, so the refetch cannot
   * write an older snapshot over them. The snapshot can already hold such an
   * event, so applying one twice must leave the data as applying it once.
   */
  onData?: (data: MaybeMasked<TData>) => void
  /**
   * Which queries to refetch after the first connect and every reconnect:
   * `true` (the default) the active queries, `false` none, a function the
   * active queries it accepts, anything else an `include` for
   * `client.refetchQueries`.
   *
   * A view mounted for the whole session should narrow this: the default
   * refetches every active query in the app on each connect.
   */
  refetchOnReconnect?: RefetchOnReconnect
  /** Runs on the first connect and on every reconnect, after that refetch starts. */
  onReconnect?: (details: RealTimeReconnectDetails) => void
  onError?: (error: ErrorLike) => void
}

export type UseBufferRealTimeSubResult<TData> = {
  /** The last event the server sent. */
  data: MaybeMasked<TData> | undefined
  status: RealTimeStatus
  error: ErrorLike | undefined
  /** Ends the open stream and opens a new one. Does nothing while closed. */
  restart: () => void
}

const requiredVariableNames = (document: DocumentNode): string[] => {
  const definition = getMainDefinition(document)
  if (definition.kind !== Kind.OPERATION_DEFINITION) {
    return []
  }

  return (definition.variableDefinitions ?? [])
    .filter(
      (variableDefinition) =>
        variableDefinition.type.kind === Kind.NON_NULL_TYPE &&
        variableDefinition.defaultValue === undefined,
    )
    .map((variableDefinition) => variableDefinition.variable.name.value)
}

type RefetchOptions = {
  include: RefetchQueriesInclude
  onQueryUpdated?: (query: ObservableQuery) => boolean
}

const refetchOptions = (
  refetchOnReconnect: RefetchOnReconnect,
): RefetchOptions | null => {
  if (refetchOnReconnect === true) {
    return { include: 'active' }
  }
  if (refetchOnReconnect === false) {
    return null
  }
  // An `include` by name also matches standby queries, and warns in
  // development for each name no mounted query has. A filter over the active
  // queries does neither.
  if (typeof refetchOnReconnect === 'function') {
    return { include: 'active', onQueryUpdated: refetchOnReconnect }
  }

  return { include: refetchOnReconnect }
}

/**
 * Subscribes one view to a GraphQL subscription, and reports the connection
 * status of that stream.
 *
 * The stream opens when the view mounts and ends when the view unmounts. The
 * server replays nothing, so the hook refetches active queries after the first
 * connect as well as after every reconnect.
 *
 * It holds the stream closed while a variable the document requires is `null`
 * or `undefined`, so a view that waits for an id needs no placeholder value.
 *
 * @example
 * ```tsx
 * const { status } = useBufferRealTimeSub(CommentAddedSubscription, {
 *   variables: { channelId },
 *   skip: !isEnabled,
 * })
 * ```
 */
export const useBufferRealTimeSub = <
  TData,
  TVariables extends OperationVariables,
>(
  subscription: TypedDocumentNode<TData, TVariables>,
  ...[options]: {} extends TVariables
    ? [options?: UseBufferRealTimeSubOptions<TData, TVariables>]
    : [options: UseBufferRealTimeSubOptions<TData, TVariables>]
): UseBufferRealTimeSubResult<TData> => {
  const {
    variables,
    skip = false,
    onData,
    refetchOnReconnect = true,
    onReconnect,
    onError,
  }: UseBufferRealTimeSubOptions<TData, OperationVariables> = options ?? {}

  const subscriptionId = React.useId()
  const client = useApolloClient()
  const { reportStatus } = useRealTimeContext()

  const requiredVariables = React.useMemo(
    () => requiredVariableNames(subscription),
    [subscription],
  )
  const missesVariable = requiredVariables.some((name) => {
    const value: unknown = variables?.[name]

    return value === undefined || value === null
  })
  const isClosed = skip || missesVariable

  const handleData = useCallbackRef(onData)
  const handleError = useCallbackRef(onError)

  // `null` while no refetch is in flight. Only the latest refetch releases the
  // held events, because an earlier one can settle before the snapshot of a
  // later connect has landed.
  const heldEvents = React.useRef<MaybeMasked<TData>[] | null>(null)
  const latestRefetch = React.useRef(0)

  // Events held for a stream the view has left belong to another scope, so a
  // refetch that settles after the view closes the stream, changes its
  // variables or unmounts must release none of them.
  const streamScope = isClosed ? null : JSON.stringify(variables ?? {})
  React.useEffect(
    () => (): void => {
      latestRefetch.current += 1
      heldEvents.current = null
    },
    [subscription, streamScope],
  )

  const handleReconnect = useCallbackRef(
    (details: RealTimeReconnectDetails): void => {
      const refetch = refetchOptions(refetchOnReconnect)
      if (refetch !== null) {
        const reportRefetchError = onError ?? logError
        const thisRefetch = ++latestRefetch.current
        heldEvents.current ??= []

        client
          .refetchQueries(refetch)
          .finally((): void => {
            if (latestRefetch.current !== thisRefetch) {
              return
            }
            const held = heldEvents.current ?? []
            heldEvents.current = null
            held.forEach((data) => handleData(data))
          })
          .catch((error: unknown): void => {
            reportRefetchError(toErrorLike(error))
          })
      }

      onReconnect?.(details)
    },
  )

  const [streamStatus, setStreamStatus] =
    React.useState<RealTimeStatus>('connecting')

  const connectionListeners = React.useMemo<RealTimeConnectionListeners>(
    () => ({
      connecting: (reconnecting): void => {
        setStreamStatus(reconnecting ? 'reconnecting' : 'connecting')
      },
      connected: (reconnected): void => {
        setStreamStatus('live')
        handleReconnect({ reconnected })
      },
    }),
    [handleReconnect],
  )

  const result = useSubscription<TData, OperationVariables>(subscription, {
    variables,
    skip: isClosed,
    context: {
      realTimeConnectionListeners: connectionListeners,
      // Apollo deduplicates two subscriptions with the same document and the
      // same variables into one link request, and keeps only the context of
      // the first one. The second view would then get no connection event, so
      // it would never refetch. Each view needs its own stream.
      queryDeduplication: false,
    },
    onData: ({ data }): void => {
      if (data.data === undefined) {
        return
      }
      if (heldEvents.current) {
        heldEvents.current.push(data.data)
        return
      }
      handleData(data.data)
    },
    onError: (error): void => {
      setStreamStatus('offline')
      handleError(error)
    },
    onComplete: (): void => {
      setStreamStatus('offline')
    },
  })

  // A view closes and opens the same subscription many times, and each open
  // starts from `connecting`. Reset during render, not in an effect: the
  // transport reports `connected` from the effect that subscribes, and a reset
  // effect after it would drop that report.
  const lastClosed = React.useRef(isClosed)
  if (lastClosed.current !== isClosed) {
    lastClosed.current = isClosed
    setStreamStatus('connecting')
  }

  const status: RealTimeStatus = isClosed ? 'idle' : streamStatus

  React.useEffect(() => {
    reportStatus(subscriptionId, status === 'idle' ? null : status)
  }, [reportStatus, subscriptionId, status])

  React.useEffect(
    () => (): void => {
      reportStatus(subscriptionId, null)
    },
    [reportStatus, subscriptionId],
  )

  // `useSubscription` returns a new result object for each event, and only the
  // `restart` it holds keeps its identity. Depend on that function, so a view
  // can pass `restart` to a memoized child.
  const restartStream = result.restart
  const restart = React.useCallback((): void => {
    if (isClosed) {
      return
    }
    restartStream()
  }, [isClosed, restartStream])

  return { data: result.data, status, error: result.error, restart }
}
